import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, of, tap } from 'rxjs';
import { EnvironmentService } from '@core/services/environment.service';
import { setCookie, deleteCookie, readCookie, resolveCookieDomain } from '@core/utils/cookie.utils';

export interface AddressNumberEntry {
	addressNumber: string;
	businessUnit: string;
}

const COOKIE_NAME = 'we_an';

@Injectable({
	providedIn: 'root',
})
export class AddressNumberService {
	private readonly http = inject(HttpClient);
	private readonly environmentService = inject(EnvironmentService);

	private readonly addressNumbersSubject = new BehaviorSubject<AddressNumberEntry[]>([]);
	public addressNumbers$ = this.addressNumbersSubject.asObservable();

	private readonly showDropdownSubject = new BehaviorSubject<boolean>(false);
	public showDropdown$ = this.showDropdownSubject.asObservable();

	private get cookieOptions() {
		return {
			domain: resolveCookieDomain(window.location.hostname, this.environmentService.isProduction),
			path: '/',
			sameSite: 'Strict' as const,
			secure: true,
		};
	}

	get showDropdown(): boolean {
		return this.showDropdownSubject.value;
	}

	private readonly selectedAddressNumberSubject = new BehaviorSubject<string | null>(this.readCookie());
	public selectedAddressNumber$ = this.selectedAddressNumberSubject.asObservable();

	get selectedAddressNumber(): string | null {
		return this.selectedAddressNumberSubject.value;
	}

	get addressNumbers(): AddressNumberEntry[] {
		return this.addressNumbersSubject.value;
	}

	loadAddressNumbers(): Observable<AddressNumberEntry[]> {
		return this.http.get<AddressNumberEntry[]>(`${this.environmentService.apiUrl}/customers/me/address-numbers`).pipe(
			tap((entries) => {
				this.addressNumbersSubject.next(entries);

				// Server returned no valid entries — remove stale cookie so the
				// interceptor does not keep sending an address-number header.
				if (entries.length === 0) {
					this.clear();
					return;
				}

				const current = this.selectedAddressNumberSubject.value;

				// Determine whether to show the dropdown:
				// Hide it when there are exactly 2 entries and one of them is CBT
				// (CBT is a shadow account — user should never need to pick it manually).
				const cbtEntries = entries.filter((e) => e.businessUnit?.toUpperCase() === 'CBT');
				const nonCbtEntries = entries.filter((e) => e.businessUnit?.toUpperCase() !== 'CBT');
				const hideBecauseCbt = entries.length === 2 && cbtEntries.length === 1;
				this.showDropdownSubject.next(!hideBecauseCbt && entries.length > 1);

				// Current selection is missing or no longer valid — pick best entry.
				if (!current || !entries.some((e) => e.addressNumber === current)) {
					// Always prefer a non-CBT entry (CBT is a shadow account).
					const preferred = nonCbtEntries.length > 0 ? nonCbtEntries[0] : entries[0];
					this.select(preferred.addressNumber);
				} else if (hideBecauseCbt) {
					// Cookie had a CBT entry selected — silently switch to the non-CBT one.
					const currentIsCbt = cbtEntries.some((e) => e.addressNumber === current);
					if (currentIsCbt && nonCbtEntries.length > 0) {
						this.select(nonCbtEntries[0].addressNumber);
					}
				}
			}),
			catchError(() => {
				// On HTTP error, preserve the current selection to avoid
				// discarding a potentially valid cookie due to a transient failure.
				this.addressNumbersSubject.next([]);
				return of([]);
			}),
		);
	}

	select(addressNumber: string): void {
		// Write cookie first so the interceptor reads the new value
		// by the time any subscriber reacts and fires HTTP requests.
		this.writeCookie(addressNumber);
		this.selectedAddressNumberSubject.next(addressNumber);
	}

	clear(): void {
		this.selectedAddressNumberSubject.next(null);
		this.addressNumbersSubject.next([]);
		this.deleteCookie();
	}

	private readCookie(): string | null {
		return readCookie(COOKIE_NAME);
	}

	private writeCookie(value: string): void {
		setCookie(COOKIE_NAME, encodeURIComponent(value), this.cookieOptions);
	}

	private deleteCookie(): void {
		deleteCookie(COOKIE_NAME, this.cookieOptions);
		// Deleting old cookie without domain — remove once no users have the legacy cookie
		deleteCookie('selected_address_number', {
			path: '/',
			sameSite: 'Strict',
			secure: true,
		});
	}
}
